import { useNavigate } from 'react-router-dom';

import useProductUpdateInputModal from 'src/features/product/product-detail/hooks/useProductUpdateInputModal';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { CTA_RFQ_SUPPORT } from 'src/shared/constants/update';
import { ProductById } from 'src/shared/types/product';

import { ButtonV2, Typography } from 'src/shared/components/ui';

export interface ProductDetailRfqRejectedProps {
  product: ProductById;
}

/**
 * Quotes tab empty state when the linked RFQ has been rejected for sourcing.
 * Shown instead of matching progress or the quotes table.
 */
const ProductDetailRfqRejected: React.FC<ProductDetailRfqRejectedProps> = ({
  product
}) => {
  const navigate = useNavigate();
  const { setData: setUpdateInputData } = useProductUpdateInputModal();

  return (
    <div>
      <Typography
        className="flex items-center gap-2 mb-4"
        variant="h4"
        as="h2"
        weight="bold"
      >
        Your product was not approved for sourcing
      </Typography>

      <div className="mt-4 rounded-lg border border-border-light-gray-2 bg-white p-6">
        <Typography variant="h6" weight="medium" className="mb-2">
          What happens now
        </Typography>
        <Typography
          variant="body-1"
          as="p"
          className="max-w-2xl text-secondary-text"
        >
          We&apos;ve reviewed this RFQ and won&apos;t be proceeding with quotes.
          Check the Updates tab for details from our team, or use the options
          below.
        </Typography>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-3 rounded-lg border border-border-light-gray-2 bg-white p-5">
          <div>
            <Typography variant="h6" weight="medium" className="mb-2">
              💬 Talk to our team
            </Typography>
            <Typography
              variant="body-1"
              as="p"
              className="text-secondary-text max-w-sm"
            >
              Want to understand more or discuss alternatives? We&apos;re here
              to help.
            </Typography>
          </div>
          <div className="w-full flex justify-end mt-auto">
            <ButtonV2
              color="primary"
              onPress={() => {
                setUpdateInputData(
                  { name: product.rfq?.document?.name ?? '' },
                  CTA_RFQ_SUPPORT
                );
              }}
            >
              Contact support
            </ButtonV2>
          </div>
        </div>

        <div className="flex flex-col gap-3 rounded-lg border border-border-light-gray-2 bg-white p-5">
          <div>
            <Typography variant="h6" weight="medium" className="mb-2">
              ✨ Start something new
            </Typography>
            <Typography
              variant="body-1"
              as="p"
              className="text-secondary-text max-w-sm"
            >
              Have another product idea? Create a new request and we&apos;ll
              take a look.
            </Typography>
          </div>
          <div className="w-full flex justify-end mt-auto">
            <ButtonV2
              onPress={() => navigate(ROUTE_PATHS.BUILD_ANYTHING)}
              color="dark"
            >
              Create new RFQ
            </ButtonV2>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailRfqRejected;
